import { FC, useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  SimpleGrid,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Avatar,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Collapse
} from '@chakra-ui/react';
import {
  MdArrowBack,
  MdEdit,
  MdVideoLibrary,
  MdLibraryBooks,
  MdQuiz,
  MdAdd,
  MdDelete,
  MdReply,
  MdExpandMore,
  MdExpandLess,
  MdPerson
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { Panel, PageHeading } from '@bemap/ui';
import { VideoContent, MarkdownContent, QuizContent } from '@bemap/university-microfrontends';
import {
  getContentById,
  getContentComments,
  getContentQuestions,
  createComment,
  updateComment,
  deleteComment,
  updateQuestion,
  deleteQuestion,
  Content,
  Comment,
  Question
} from '@/services/university-service';

const ViewContentPage: FC = () => {
  const navigate = useNavigate();
  const { courseId, moduleId, contentId } = useParams<{ courseId: string; moduleId: string; contentId: string }>();
  const toast = useToast();
  const { t } = useTranslation();
  const [content, setContent] = useState<Content | null>(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [replyToComment, setReplyToComment] = useState<Comment | null>(null);
  const [replyText, setReplyText] = useState('');
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());

  const { isOpen: isCommentModalOpen, onOpen: onCommentModalOpen, onClose: onCommentModalClose } = useDisclosure();
  const { isOpen: isQuestionModalOpen, onOpen: onQuestionModalOpen, onClose: onQuestionModalClose } = useDisclosure();
  const { isOpen: isReplyModalOpen, onOpen: onReplyModalOpen, onClose: onReplyModalClose } = useDisclosure();

  const cardBg = useColorModeValue('white', 'gray.800');

  // Load content data from API
  useEffect(() => {
    const loadContent = async () => {
      if (!contentId) return;

      try {
        setLoading(true);
        const response = await getContentById(parseInt(contentId));

        if (response.statusCode === 200 && response.message) {
          setContent(response.message as Content);
        } else {
          throw new Error(response.message || t('university.viewContent.error.loading_content'));
        }
      } catch (error: any) {
        console.error('Error loading content:', error);
        toast({
          title: t('university.viewContent.error.loading_content'),
          description: error.message || t('university.viewContent.error.loading_content_description'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [contentId, courseId, navigate, toast]);

  // Load comments
  const loadComments = async () => {
    if (!contentId) return;

    try {
      setCommentsLoading(true);
      const response = await getContentComments(parseInt(contentId));

      if (response.statusCode === 200 && response.message) {
        setComments(response.message as Comment[]);
      } else {
        throw new Error(response.message || t('university.viewContent.error.loading_comments'));
      }
    } catch (error: any) {
      console.error('Error loading comments:', error);
      toast({
        title: t('university.viewContent.error.loading_comments'),
        description: error.message || t('university.viewContent.error.loading_comments_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setCommentsLoading(false);
    }
  };

  // Load questions
  const loadQuestions = async () => {
    if (!contentId) return;

    try {
      setQuestionsLoading(true);
      const response = await getContentQuestions(parseInt(contentId));

      if (response.statusCode === 200 && response.message) {
        setQuestions(response.message as Question[]);
      } else {
        throw new Error(response.message || t('university.viewContent.error.loading_questions'));
      }
    } catch (error: any) {
      console.error('Error loading questions:', error);
      toast({
        title: t('university.viewContent.error.loading_questions'),
        description: error.message || t('university.viewContent.error.loading_questions_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Handle comment submission
  const handleSubmitComment = async () => {
    if (!contentId || !newComment.trim()) return;

    try {
      const response = await createComment({
        content: newComment.trim(),
        contentId: parseInt(contentId),
        userId: 1, // TODO: Get from auth context
        active: true
      });

      if (response.statusCode === 201) {
        toast({
          title: t('university.viewContent.success.comment_added'),
          description: t('university.viewContent.success.comment_added_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setNewComment('');
        loadComments();
      } else {
        throw new Error(response.message || t('university.viewContent.error.adding_comment'));
      }
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast({
        title: t('university.viewContent.error.adding_comment'),
        description: error.message || t('university.viewContent.error.adding_comment_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle reply submission
  const handleSubmitReply = async () => {
    if (!contentId || !replyText.trim() || !replyToComment) return;

    try {
      const response = await createComment({
        content: replyText.trim(),
        contentId: parseInt(contentId),
        userId: 1, // TODO: Get from auth context
        parentId: replyToComment.id,
        active: true
      });

      if (response.statusCode === 201) {
        toast({
          title: t('university.viewContent.success.reply_added'),
          description: t('university.viewContent.success.reply_added_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setReplyText('');
        setReplyToComment(null);
        onReplyModalClose();
        loadComments();
      } else {
        throw new Error(response.message || t('university.viewContent.error.adding_reply'));
      }
    } catch (error: any) {
      console.error('Error adding reply:', error);
      toast({
        title: t('university.viewContent.error.adding_reply'),
        description: error.message || t('university.viewContent.error.adding_reply_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle comment edit
  const handleEditComment = async () => {
    if (!editingComment || !editingComment.content.trim()) return;

    try {
      const response = await updateComment(editingComment.id, {
        content: editingComment.content.trim()
      });

      if (response.statusCode === 200) {
        toast({
          title: t('university.viewContent.success.comment_updated'),
          description: t('university.viewContent.success.comment_updated_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setEditingComment(null);
        onCommentModalClose();
        loadComments();
      } else {
        throw new Error(response.message || t('university.viewContent.error.updating_comment'));
      }
    } catch (error: any) {
      console.error('Error updating comment:', error);
      toast({
        title: t('university.viewContent.error.updating_comment'),
        description: error.message || t('university.viewContent.error.updating_comment_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle question edit
  const handleEditQuestion = async () => {
    if (!editingQuestion || !editingQuestion.content.trim()) return;

    try {
      const response = await updateQuestion(editingQuestion.id, {
        content: editingQuestion.content.trim()
      });

      if (response.statusCode === 200) {
        toast({
          title: t('university.viewContent.success.question_updated'),
          description: t('university.viewContent.success.question_updated_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setEditingQuestion(null);
        onQuestionModalClose();
        loadQuestions();
      } else {
        throw new Error(response.message || t('university.viewContent.error.updating_question'));
      }
    } catch (error: any) {
      console.error('Error updating question:', error);
      toast({
        title: t('university.viewContent.error.updating_question'),
        description: error.message || t('university.viewContent.error.updating_question_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle comment deletion
  const handleDeleteComment = async (commentId: number) => {
    if (!confirm(t('university.viewContent.confirm_delete_comment'))) return;

    try {
      const response = await deleteComment(commentId);

      if (response.statusCode === 200) {
        toast({
          title: t('university.viewContent.success.comment_deleted'),
          description: t('university.viewContent.success.comment_deleted_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        loadComments();
      } else {
        throw new Error(response.message || t('university.viewContent.error.deleting_comment'));
      }
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      toast({
        title: t('university.viewContent.error.deleting_comment'),
        description: error.message || t('university.viewContent.error.deleting_comment_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle question deletion
  const handleDeleteQuestion = async (questionId: number) => {
    if (!confirm(t('university.viewContent.confirm_delete_question'))) return;

    try {
      const response = await deleteQuestion(questionId);

      if (response.statusCode === 200) {
        toast({
          title: t('university.viewContent.success.question_deleted'),
          description: t('university.viewContent.success.question_deleted_description'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        loadQuestions();
      } else {
        throw new Error(response.message || t('university.viewContent.error.deleting_question'));
      }
    } catch (error: any) {
      console.error('Error deleting question:', error);
      toast({
        title: t('university.viewContent.error.deleting_question'),
        description: error.message || t('university.viewContent.error.deleting_question_description'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Toggle comment expansion
  const toggleCommentExpansion = (commentId: number) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId);
    } else {
      newExpanded.add(commentId);
    }
    setExpandedComments(newExpanded);
  };

  // Toggle question expansion
  const toggleQuestionExpansion = (questionId: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleBack = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
  };

  const handleEditContent = () => {
    // Navigate to edit content page based on content type
    switch (content?.type) {
      case 'video':
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_VIDEO_EDIT.replace(':courseId', courseId!).replace(':moduleId', moduleId!).replace(':contentId', contentId!));
        break;
      case 'document':
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_DOCUMENT_EDIT.replace(':courseId', courseId!).replace(':moduleId', moduleId!).replace(':contentId', contentId!));
        break;
      case 'quiz':
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_QUIZ_EDIT.replace(':courseId', courseId!).replace(':moduleId', moduleId!).replace(':contentId', contentId!));
        break;
      default:
        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
    }
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video': return MdVideoLibrary;
      case 'document': return MdLibraryBooks;
      case 'quiz': return MdQuiz;
      default: return MdLibraryBooks;
    }
  };







  const renderContent = () => {
    if (!content) return null;

    // Convert content to the format expected by microfrontend components
    const baseContentItem = {
      id: content.id.toString(),
      title: content.title,
      type: content.type,
      duration: content.duration,
      completed: content.completed,
      active: content.active,
    };

    switch (content.type) {
      case 'video':
        return (
          <Box p={4}>
            <VideoContent
              content={{ ...baseContentItem, data: content.data }}
              onComplete={() => {
                // Admin view doesn't need completion handling
              }}
            />
          </Box>
        );
      case 'document':
        return (
          <Box p={4}>
            <MarkdownContent
              content={{ ...baseContentItem, data: content.data }}
              onComplete={() => {
                // Admin view doesn't need completion handling
              }}
            />
          </Box>
        );
      case 'quiz':
        return (
          <Box p={4}>
            <QuizContent
              content={{ ...baseContentItem, data: content.data as any }}
              t={(key: string) => key} // Simple translation function for admin
            />
          </Box>
        );
      default:
        return <Text color="gray.500">{t('university.viewContent.content_type_not_supported')}</Text>;
    }
  };

  // Comments Tab Component
  const CommentsTab = () => (
    <VStack align="stretch" spacing={4}>
      {/* Add Comment Section */}
      <Card bg={cardBg} p={4}>
        <VStack align="stretch" spacing={3}>
          <Text fontWeight="semibold">{t('university.viewContent.add_comment')}</Text>
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('university.viewContent.write_comment_placeholder')}
            rows={3}
          />
          <HStack justify="flex-end">
            <Button
              colorScheme="blue"
              onClick={handleSubmitComment}
              isDisabled={!newComment.trim()}
              leftIcon={<Icon as={MdAdd} />}
            >
              {t('university.viewContent.add_comment_button')}
            </Button>
          </HStack>
        </VStack>
      </Card>

      {/* Comments List */}
      {commentsLoading ? (
        <Center py={8}>
          <VStack spacing={3}>
            <Spinner size="lg" color="blue.500" />
            <Text>{t('university.viewContent.loading_comments')}</Text>
          </VStack>
        </Center>
      ) : comments.length === 0 ? (
        <Card bg={cardBg} p={6}>
          <Center>
            <VStack spacing={3}>
              <Icon as={MdPerson} boxSize={8} color="gray.400" />
              <Text color="gray.500">{t('university.viewContent.no_comments')}</Text>
              <Text fontSize="sm" color="gray.400">{t('university.viewContent.no_comments_description')}</Text>
            </VStack>
          </Center>
        </Card>
      ) : (
        <VStack align="stretch" spacing={4}>
          {comments.map((comment) => (
            <Card key={comment.id} bg={cardBg} p={4}>
              <VStack align="stretch" spacing={3}>
                {/* Comment Header */}
                <HStack justify="space-between" align="start">
                  <HStack spacing={3}>
                    <Avatar size="sm" name={comment.user?.name || 'User'} />
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold" fontSize="sm">
                        {comment.user?.name || t('university.viewContent.anonymous')}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(comment.createdAt).toLocaleDateString()} at {new Date(comment.createdAt).toLocaleTimeString()}
                      </Text>
                    </VStack>
                  </HStack>
                  <HStack spacing={1}>
                    {comment.replies && comment.replies.length > 0 && (
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={<Icon as={expandedComments.has(comment.id) ? MdExpandLess : MdExpandMore} />}
                        onClick={() => toggleCommentExpansion(comment.id)}
                        aria-label={t('university.viewContent.toggle_replies')}
                      />
                    )}
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Icon as={MdEdit} />}
                      onClick={() => {
                        setEditingComment(comment);
                        onCommentModalOpen();
                      }}
                      aria-label={t('university.viewContent.edit_comment')}
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      icon={<Icon as={MdDelete} />}
                      onClick={() => handleDeleteComment(comment.id)}
                      aria-label={t('university.viewContent.delete_comment')}
                    />
                  </HStack>
                </HStack>

                {/* Comment Content */}
                <Text>{comment.content}</Text>

                {/* Reply Button */}
                <HStack justify="flex-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Icon as={MdReply} />}
                    onClick={() => {
                      setReplyToComment(comment);
                      onReplyModalOpen();
                    }}
                  >
                    {t('university.viewContent.reply')}
                  </Button>
                </HStack>

                {/* Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <Collapse in={expandedComments.has(comment.id)}>
                    <VStack align="stretch" spacing={3} pl={6} borderLeft="2px" borderColor="gray.200">
                      {comment.replies.map((reply) => (
                        <Card key={reply.id} bg="gray.50" p={3}>
                          <VStack align="stretch" spacing={2}>
                            <HStack justify="space-between" align="start">
                              <HStack spacing={2}>
                                <Avatar size="xs" name={reply.user?.name || 'User'} />
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="semibold" fontSize="xs">
                                    {reply.user?.name || t('university.viewContent.anonymous')}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {new Date(reply.createdAt).toLocaleDateString()} at {new Date(reply.createdAt).toLocaleTimeString()}
                                  </Text>
                                </VStack>
                              </HStack>
                              <HStack spacing={1}>
                                <IconButton
                                  size="xs"
                                  variant="ghost"
                                  icon={<Icon as={MdEdit} />}
                                  onClick={() => {
                                    setEditingComment(reply);
                                    onCommentModalOpen();
                                  }}
                                  aria-label={t('university.viewContent.edit_reply')}
                                />
                                <IconButton
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  icon={<Icon as={MdDelete} />}
                                  onClick={() => handleDeleteComment(reply.id)}
                                  aria-label={t('university.viewContent.delete_reply')}
                                />
                              </HStack>
                            </HStack>
                            <Text fontSize="sm">{reply.content}</Text>
                          </VStack>
                        </Card>
                      ))}
                    </VStack>
                  </Collapse>
                )}
              </VStack>
            </Card>
          ))}
        </VStack>
      )}
    </VStack>
  );

  // Questions Tab Component
  const QuestionsTab = () => (
    <VStack align="stretch" spacing={4}>
      {/* Questions List */}
      {questionsLoading ? (
        <Center py={8}>
          <VStack spacing={3}>
            <Spinner size="lg" color="blue.500" />
            <Text>{t('university.viewContent.loading_questions')}</Text>
          </VStack>
        </Center>
      ) : questions.length === 0 ? (
        <Card bg={cardBg} p={6}>
          <Center>
            <VStack spacing={3}>
              <Icon as={MdPerson} boxSize={8} color="gray.400" />
              <Text color="gray.500">{t('university.viewContent.no_questions')}</Text>
              <Text fontSize="sm" color="gray.400">{t('university.viewContent.no_questions_description')}</Text>
            </VStack>
          </Center>
        </Card>
      ) : (
        <VStack align="stretch" spacing={4}>
          {questions.map((question) => (
            <Card key={question.id} bg={cardBg} p={4}>
              <VStack align="stretch" spacing={3}>
                {/* Question Header */}
                <HStack justify="space-between" align="start">
                  <HStack spacing={3}>
                    <Avatar size="sm" name={question.user?.name || 'User'} />
                    <VStack align="start" spacing={0}>
                      <HStack spacing={2}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {question.user?.name || t('university.viewContent.anonymous')}
                        </Text>
                        <Badge
                          colorScheme={question.isAnswered ? 'green' : 'orange'}
                          size="sm"
                        >
                          {question.isAnswered ? t('university.viewContent.answered') : t('university.viewContent.pending')}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(question.createdAt).toLocaleDateString()} at {new Date(question.createdAt).toLocaleTimeString()}
                      </Text>
                    </VStack>
                  </HStack>
                  <HStack spacing={1}>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Icon as={MdExpandMore} />}
                      onClick={() => toggleQuestionExpansion(question.id)}
                      aria-label={t('university.viewContent.toggle_details')}
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Icon as={MdEdit} />}
                      onClick={() => {
                        setEditingQuestion(question);
                        onQuestionModalOpen();
                      }}
                      aria-label={t('university.viewContent.edit_question')}
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      icon={<Icon as={MdDelete} />}
                      onClick={() => handleDeleteQuestion(question.id)}
                      aria-label={t('university.viewContent.delete_question')}
                    />
                  </HStack>
                </HStack>

                {/* Question Content */}
                <Text>{question.content}</Text>

                {/* Admin Reply */}
                {question.isAnswered && question.adminReply && (
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">{t('university.viewContent.admin_reply')}</AlertTitle>
                      <AlertDescription fontSize="sm">
                        {question.adminReply}
                      </AlertDescription>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {t('university.viewContent.replied_by')} {question.adminUser?.name || 'Admin'} {t('university.viewContent.on')} {question.adminReplyAt ? new Date(question.adminReplyAt).toLocaleDateString() : t('university.viewContent.unknown_date')}
                      </Text>
                    </Box>
                  </Alert>
                )}

                {/* Question Details (Expanded) */}
                <Collapse in={expandedQuestions.has(question.id)}>
                  <VStack align="stretch" spacing={2} pt={2} borderTop="1px" borderColor="gray.200">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>{t('university.viewContent.status')}</Text>
                        <Badge colorScheme={question.isAnswered ? 'green' : 'orange'}>
                          {question.isAnswered ? t('university.viewContent.answered') : t('university.viewContent.pending')}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>{t('university.viewContent.created_date')}</Text>
                        <Text fontSize="sm">{new Date(question.createdAt).toLocaleDateString()}</Text>
                      </Box>
                      {question.isAnswered && (
                        <>
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>{t('university.viewContent.answered_by')}</Text>
                            <Text fontSize="sm">{question.adminUser?.name || 'Admin'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>{t('university.viewContent.answered_on')}</Text>
                            <Text fontSize="sm">{question.adminReplyAt ? new Date(question.adminReplyAt).toLocaleDateString() : t('university.viewContent.unknown')}</Text>
                          </Box>
                        </>
                      )}
                    </SimpleGrid>
                  </VStack>
                </Collapse>
              </VStack>
            </Card>
          ))}
        </VStack>
      )}
    </VStack>
  );

  if (loading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>{t('university.viewContent.loading_content')}</Text>
        </VStack>
      </Center>
    );
  }

  if (!content) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Text>{t('university.viewContent.content_not_found')}</Text>
          <Button onClick={handleBack}>{t('university.viewContent.back_to_course')}</Button>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={content.title} actions={[
        {
          label: t('university.viewContent.back_to_course'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        },
        {
          label: t('university.viewContent.edit_content'),
          onClick: handleEditContent,
          icon: <Icon as={MdEdit} />,
          variant: 'outline'
        }
      ]} />

      {/* Content Overview */}
      <Panel heading={t('university.viewContent.content_overview')} padding={6}>
        <VStack align="stretch" spacing={6}>
          <HStack spacing={4} align="center">
            <Icon as={getContentIcon(content.type)} color="blue.500" boxSize={6} />
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="semibold">{content.title}</Text>
              <HStack spacing={3}>
                <Badge colorScheme={content.completed ? 'green' : 'gray'}>
                  {content.completed ? t('university.viewContent.completed') : t('university.viewContent.pending')}
                </Badge>
                <Badge colorScheme={content.active ? 'blue' : 'gray'}>
                  {content.active ? t('university.viewContent.active') : t('university.viewContent.inactive')}
                </Badge>
                <Badge colorScheme="purple">
                  {content.type}
                </Badge>
              </HStack>
            </VStack>
          </HStack>

          <Divider />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>{t('university.viewContent.duration')}</Text>
              <Text>{content.duration || t('university.viewContent.not_specified')}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>{t('university.viewContent.order')}</Text>
              <Text>{content.order}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>{t('university.viewContent.created')}</Text>
              <Text>{new Date(content.createdAt).toLocaleDateString()}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>{t('university.viewContent.last_updated')}</Text>
              <Text>{new Date(content.updatedAt).toLocaleDateString()}</Text>
            </Box>
          </SimpleGrid>
        </VStack>
      </Panel>

      {/* Content Display */}
      <Panel heading={t('university.viewContent.content_preview')} padding={6}>
        <Tabs colorScheme="blue" onChange={(index) => {
          if (index === 1) loadComments();
          if (index === 2) loadQuestions();
        }}>
          <TabList>
            <Tab>{t('university.viewContent.preview')}</Tab>
            <Tab>{t('university.viewContent.comments')} ({comments.length})</Tab>
            <Tab>{t('university.viewContent.questions')} ({questions.length})</Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={0}>
              {renderContent()}
            </TabPanel>

            <TabPanel>
              <CommentsTab />
            </TabPanel>

            <TabPanel>
              <QuestionsTab />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Panel>

      {/* Edit Comment Modal */}
      <Modal isOpen={isCommentModalOpen} onClose={onCommentModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('university.viewContent.edit_comment_modal')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={editingComment?.content || ''}
              onChange={(e) => setEditingComment(prev => prev ? { ...prev, content: e.target.value } : null)}
              placeholder={t('university.viewContent.edit_comment_placeholder')}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCommentModalClose}>
              {t('university.viewContent.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleEditComment}
              isDisabled={!editingComment?.content.trim()}
            >
              {t('university.viewContent.update_comment')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Question Modal */}
      <Modal isOpen={isQuestionModalOpen} onClose={onQuestionModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('university.viewContent.edit_question_modal')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={editingQuestion?.content || ''}
              onChange={(e) => setEditingQuestion(prev => prev ? { ...prev, content: e.target.value } : null)}
              placeholder={t('university.viewContent.edit_question_placeholder')}
              rows={4}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onQuestionModalClose}>
              {t('university.viewContent.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleEditQuestion}
              isDisabled={!editingQuestion?.content.trim()}
            >
              {t('university.viewContent.update_question')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reply Modal */}
      <Modal isOpen={isReplyModalOpen} onClose={onReplyModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('university.viewContent.reply_modal')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">
                {t('university.viewContent.replying_to')} {replyToComment?.user?.name || t('university.viewContent.anonymous')}
              </Text>
              <Text fontSize="sm" fontStyle="italic" color="gray.500">
                "{replyToComment?.content}"
              </Text>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={t('university.viewContent.write_reply_placeholder')}
                rows={4}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onReplyModalClose}>
              {t('university.viewContent.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmitReply}
              isDisabled={!replyText.trim()}
            >
              {t('university.viewContent.add_reply')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ViewContentPage; 